import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, onSnapshot, query, orderBy, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import {
    Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, Grid, InputAdornment, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import styles from '../styles/Dashboard.module.css';

const GerenciarFornecedores = () => {
    const [fornecedores, setFornecedores] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        nome: '',
        telefone: '',
        email: '',
        categoria: ''
    });

    useEffect(() => {
        const q = query(collection(db, 'tb_fornecedores'), orderBy('nome'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setFornecedores(list);
        });
        return () => unsubscribe();
    }, []);

    const handleOpenCreate = () => {
        setIsEdit(false);
        setSelectedId(null);
        setFormData({ nome: '', telefone: '', email: '', categoria: '' });
        setOpenDialog(true);
    };

    const handleOpenEdit = (item) => {
        setIsEdit(true);
        setSelectedId(item.id);
        setFormData({
            nome: item.nome,
            telefone: item.telefone,
            email: item.email || '',
            categoria: item.categoria || '',
            api_url: item.api_url || '',
            consumer_key: item.consumer_key || '',
            consumer_secret: item.consumer_secret || ''
        });
        setOpenDialog(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este fornecedor?')) {
            try {
                await deleteDoc(doc(db, 'tb_fornecedores', id));
            } catch (error) {
                console.error("Erro ao excluir:", error);
                alert("Erro ao excluir fornecedor.");
            }
        }
    };

    const handleSave = async () => {
        if (!formData.nome || !formData.telefone) {
            alert("Nome e Telefone são obrigatórios.");
            return;
        }

        try {
            if (isEdit) {
                await updateDoc(doc(db, 'tb_fornecedores', selectedId), {
                    nome: formData.nome,
                    telefone: formData.telefone,
                    email: formData.email,
                    categoria: formData.categoria,
                    api_url: formData.api_url || '',
                    consumer_key: formData.consumer_key || '',
                    consumer_secret: formData.consumer_secret || ''
                });
            } else {
                await addDoc(collection(db, 'tb_fornecedores'), {
                    nome: formData.nome,
                    telefone: formData.telefone,
                    email: formData.email,
                    categoria: formData.categoria,
                    api_url: formData.api_url || '',
                    consumer_key: formData.consumer_key || '',
                    consumer_secret: formData.consumer_secret || '',
                    created_at: serverTimestamp()
                });
            }
            setOpenDialog(false);
        } catch (error) {
            console.error("Erro ao salvar:", error);
            alert("Erro ao salvar fornecedor.");
        }
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 3 } }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
                Gerenciar Fornecedores
            </Typography>

            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
                sx={{ mb: 3, borderRadius: '20px', textTransform: 'none' }}
            >
                Novo Fornecedor
            </Button>

            <TableContainer component={Paper} elevation={3} sx={{ borderRadius: '12px' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell><strong>Nome</strong></TableCell>
                            <TableCell><strong>Telefone / WhatsApp</strong></TableCell>
                            <TableCell><strong>Categoria</strong></TableCell>
                            <TableCell align="right"><strong>Ações</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fornecedores.map((item) => (
                            <TableRow key={item.id} hover>
                                <TableCell>{item.nome}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <WhatsAppIcon color="success" fontSize="small" />
                                        {item.telefone}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {item.categoria && <Chip label={item.categoria} size="small" />}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => handleOpenEdit(item)} color="primary">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleDelete(item.id)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {fornecedores.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">Nenhum fornecedor cadastrado.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog Create/Edit */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>{isEdit ? 'Editar Fornecedor' : 'Novo Fornecedor'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Nome da Empresa / Fornecedor"
                                value={formData.nome}
                                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="WhatsApp (com DDD e 55)"
                                placeholder="5511999999999"
                                value={formData.telefone}
                                onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                                required
                                helperText="Apenas números. Formato internacional recomendado."
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Categoria Principal (Opcional)"
                                placeholder="Ex: Limpeza, Elétrica..."
                                value={formData.categoria}
                                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Email (Opcional)"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </Grid>

                        {/* WooCommerce Integration Section */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                                Integração WooCommerce (Opcional)
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                                Preencha para importar produtos diretamente da loja deste fornecedor.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="URL da Loja (ex: https://loja.com)"
                                placeholder="https://..."
                                value={formData.api_url || ''}
                                onChange={(e) => setFormData({ ...formData, api_url: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Consumer Key (ck_...)"
                                value={formData.consumer_key || ''}
                                onChange={(e) => setFormData({ ...formData, consumer_key: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Consumer Secret (cs_...)"
                                type="password"
                                value={formData.consumer_secret || ''}
                                onChange={(e) => setFormData({ ...formData, consumer_secret: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GerenciarFornecedores;
